import React from "react";
import RevealAnimation from "../RevealAnimation";
import { Link } from "gatsby";
import "./whitePapersHeroBanner.scss";

/**
 * Unique hero banner for /insights/white-papers
 * Cool professional theme with blue/indigo accents
 */
function WhitePapersHeroBanner({
  title = "White Papers",
  subtitle = "",
  breadcrumbs = [],
  totalPapers = 0,
}) {
  return (
    <section className="wp-hero-banner">
      {/* Background */}
      <div className="wp-hero-bg">
        <div className="wp-gradient-base" />
        <div className="wp-grid-overlay" />
        <div className="wp-glow wp-glow--1" />
        <div className="wp-glow wp-glow--2" />
        <div className="wp-glow wp-glow--3" />
        {/* Geometric shapes */}
        <div className="wp-shapes">
          <div className="wp-shape wp-shape--hex" />
          <div className="wp-shape wp-shape--circle" />
          <div className="wp-shape wp-shape--ring" />
        </div>
      </div>

      <div className="wp-hero-inner body-container">
        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <RevealAnimation
            component="nav"
            className="wp-hero-breadcrumbs"
            delay={0}
            animation="fade"
          >
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <React.Fragment key={i}>
                  {isLast ? (
                    <span className="crumb crumb--active">{crumb.label}</span>
                  ) : (
                    <>
                      <Link to={crumb.to} className="crumb">
                        {crumb.label}
                      </Link>
                      <span className="crumb-sep">/</span>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </RevealAnimation>
        )}

        <div className="wp-hero-content">
          <div className="wp-hero-left">
            {/* Category badge */}
            <RevealAnimation
              component="div"
              className="wp-category-badge"
              delay={0.05}
              animation="fade"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              Research & White Papers
            </RevealAnimation>

            {/* Title */}
            <RevealAnimation
              component="h1"
              className="wp-hero-title"
              delay={0.1}
              animation="slide-up"
            >
              In-Depth <span className="title-accent">White Papers</span>
              <br />& Research Reports
            </RevealAnimation>

            {/* Subtitle */}
            {subtitle && (
              <RevealAnimation
                component="p"
                className="wp-hero-subtitle"
                delay={0.2}
                animation="slide-up"
              >
                {subtitle}
              </RevealAnimation>
            )}

            {/* Nav pills */}
            <RevealAnimation
              component="div"
              className="wp-hero-nav"
              delay={0.25}
              animation="slide-up"
            >
              <Link to="/insights/articles" className="wp-pill">
                Articles
              </Link>
              <Link
                to="/insights/white-papers"
                className="wp-pill wp-pill--active"
              >
                <span className="wp-pill-dot" />
                White Papers
              </Link>
              <Link to="/insights" className="wp-pill">
                All Insights
              </Link>
            </RevealAnimation>
          </div>

          {/* Right side - document visual */}
          <div className="wp-hero-right">
            <RevealAnimation
              className="wp-hero-visual"
              delay={0.3}
              animation="fade"
            >
              <div className="wp-doc-visual">
                {/* Floating document */}
                <div className="wp-doc-card">
                  <div className="wp-doc-header">
                    <div className="wp-doc-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                      </svg>
                    </div>
                    <div className="wp-doc-badge">PDF</div>
                  </div>
                  <div className="wp-doc-lines">
                    <div className="wp-doc-line wp-doc-line--title" />
                    <div className="wp-doc-line wp-doc-line--title-short" />
                    <div className="wp-doc-divider" />
                    <div className="wp-doc-line wp-doc-line--body" />
                    <div className="wp-doc-line wp-doc-line--body" />
                    <div className="wp-doc-line wp-doc-line--body-short" />
                    <div className="wp-doc-chart">
                      <div className="chart-bar chart-bar--1" />
                      <div className="chart-bar chart-bar--2" />
                      <div className="chart-bar chart-bar--3" />
                      <div className="chart-bar chart-bar--4" />
                      <div className="chart-bar chart-bar--5" />
                    </div>
                  </div>
                </div>

                {/* Background card */}
                <div className="wp-doc-shadow-card" />

                {/* Counter badge */}
                {totalPapers > 0 && (
                  <div className="wp-count-badge">
                    <span className="wp-count-value">{totalPapers}+</span>
                    <span className="wp-count-label">Papers</span>
                  </div>
                )}
              </div>
            </RevealAnimation>
          </div>
        </div>
      </div>

      {/* Bottom accent */}
      <div className="wp-hero-bottom">
        <svg
          viewBox="0 0 1440 50"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0 50V25C360 45 720 5 1080 25C1260 35 1380 40 1440 35V50H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
}

export default WhitePapersHeroBanner;
